import {
  useGetAdmissionTiers,
  useGetUniversitiesLight,
  useSeedAdmissionTiers,
  useUpdateAdmissionTiers,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Select from '@components/V4/Select/Select';
import { DownloadIcon, TrashIcon } from '@heroicons/react/outline';
import { useDebounce } from '@hooks';
import {
  EmptyState,
  FilterField,
  SectionHeading,
  TableShell,
} from '@modules/Admin/Reporting/common';
import { AdmissionTierUniversityDTO } from '@shared/clientTypes/DTOs/AdminMetricsDTO';
import { ADMISSION_TIER_LABELS, ADMISSION_TIERS, AdmissionTier } from '@shared/constants';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import { stringify } from 'csv-stringify/sync';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { downloadCsv } from './admissionStats.helpers';

const headRow = 'border-b border-white/10 text-xs uppercase tracking-wide text-customGrey';
const bodyRow = 'border-b border-white/5 align-top last:border-b-0';
const leftHead = 'px-4 py-2.5 text-left font-semibold';
const leftCell = 'px-4 py-2.5 text-left text-customLight';

const TierToggle = (props: { tier: AdmissionTier; active: boolean; onToggle: () => void }) => (
  <button
    type="button"
    onClick={props.onToggle}
    className={mergeClassNames(
      'rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors',
      props.active
        ? 'border-customPrimary bg-customPrimary/20 text-customPrimary'
        : 'border-white/15 text-customGrey hover:border-white/40 hover:text-white'
    )}
  >
    {ADMISSION_TIER_LABELS[props.tier]}
  </button>
);

const TierRow = (props: { university: AdmissionTierUniversityDTO; onSaved: () => void }) => {
  const { university, onSaved } = props;
  const toast = useToast();
  const { mutate: update, isLoading } = useUpdateAdmissionTiers(university._id, {
    onSuccess: onSaved,
    onError: () => toast.error('Could not save the tiers.'),
  });
  const toggle = (tier: AdmissionTier) =>
    update({
      tiers: university.tiers.includes(tier)
        ? university.tiers.filter((item) => item !== tier)
        : [...university.tiers, tier],
    });

  return (
    <tr className={bodyRow}>
      <td className={leftCell}>
        <span className="font-semibold text-white">{university.name}</span>
        <span className="block text-xs text-customGrey">
          {university.country}
          {university.region ? ` · ${university.region}` : ''}
          {university.rank ? ` · QS #${university.rank}` : ' · no QS rank'}
        </span>
      </td>
      <td className={leftCell}>
        <div className={mergeClassNames('flex flex-wrap gap-1.5', isLoading && 'opacity-50')}>
          {ADMISSION_TIERS.map((tier) => (
            <TierToggle
              key={tier}
              tier={tier}
              active={university.tiers.includes(tier)}
              onToggle={() => toggle(tier)}
            />
          ))}
        </div>
      </td>
      <td className="px-4 py-2.5 text-right">
        <button
          type="button"
          className="text-customGrey hover:text-customRed"
          title="Remove from all tiers"
          onClick={() => update({ tiers: [] })}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      </td>
    </tr>
  );
};

const TiersTab = () => {
  const toast = useToast();
  const { data, isLoading, refetch } = useGetAdmissionTiers();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 400);
  const [pendingId, setPendingId] = useState<string>();
  const [filterTier, setFilterTier] = useState<AdmissionTier | 'all'>('all');

  const { data: candidates, isLoading: isSearching } = useGetUniversitiesLight(
    { search: debouncedSearch, limit: 20 },
    { enabled: debouncedSearch.trim().length >= 2 }
  );

  const { mutate: addTier, isLoading: isAdding } = useUpdateAdmissionTiers(pendingId ?? '', {
    onSuccess: () => {
      toast.success('University added.');
      setPendingId(undefined);
      setSearch('');
      refetch();
    },
    onError: () => toast.error('Could not add the university.'),
  });

  const { mutate: seed, isLoading: isSeeding } = useSeedAdmissionTiers({
    onSuccess: (result) => {
      toast.success(
        `Default lists applied: ${result.updated} universities updated${
          result.unmatched.length
            ? `, ${result.unmatched.length} names not found in our database`
            : ''
        }.`
      );
      refetch();
    },
    onError: () => toast.error('Could not apply the default lists.'),
  });

  const universities = useMemo(
    () =>
      (data?.universities ?? []).filter(
        (university) => filterTier === 'all' || university.tiers.includes(filterTier)
      ),
    [data, filterTier]
  );

  const counts = useMemo(
    () =>
      ADMISSION_TIERS.map((tier) => ({
        tier,
        count: (data?.universities ?? []).filter((university) => university.tiers.includes(tier))
          .length,
      })),
    [data]
  );

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <LoadingSpinner size="normal" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <SectionHeading
          title="University tiers"
          description="Which universities count as Ivy League, Oxbridge, Tier I UK and Tier I EU in the stats. Saved on the university record in our database, so changes apply everywhere immediately."
        />
        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            buttonText={isSeeding ? 'Applying…' : 'Apply default lists'}
            isLoading={isSeeding}
            disabled={isSeeding}
            onClick={() => seed({})}
          />
          <Button
            variant="secondary"
            buttonText="Export CSV"
            icon={<DownloadIcon className="h-4 w-4" />}
            disabled={!universities.length}
            onClick={() =>
              downloadCsv(
                `admission-tiers-${moment().format('YYYY-MM-DD')}.csv`,
                stringify([
                  ['University', 'Country', 'Region', 'QS rank', 'Tiers'],
                  ...universities.map((university) => [
                    university.name,
                    university.country,
                    university.region ?? '',
                    university.rank ?? '',
                    university.tiers
                      .map((tier) => ADMISSION_TIER_LABELS[tier as AdmissionTier])
                      .join('; '),
                  ]),
                ])
              )
            }
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {counts.map(({ tier, count }) => (
          <button
            key={tier}
            type="button"
            onClick={() => setFilterTier(filterTier === tier ? 'all' : tier)}
            className={mergeClassNames(
              'rounded-full px-3 py-1 text-xs font-semibold',
              filterTier === tier ? 'bg-customPrimary text-black' : 'bg-white/10 text-customLight'
            )}
          >
            {ADMISSION_TIER_LABELS[tier]} · {count}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <FilterField width="w-full tablet:w-96">
          <Select
            label="Add a university from our database"
            variant="secondary"
            showSearch
            filterOption={() => true}
            searchValue={search}
            onSearch={setSearch}
            isLoading={isSearching}
            value={pendingId}
            options={(candidates ?? []).map((university) => ({
              value: university._id,
              label: university.name,
            }))}
            onSelect={(value) => setPendingId(value ? String(value) : undefined)}
            placeholder="Type at least 2 letters"
          />
        </FilterField>
        {ADMISSION_TIERS.map((tier) => (
          <Button
            key={tier}
            variant="secondary"
            buttonText={`Add as ${ADMISSION_TIER_LABELS[tier]}`}
            disabled={!pendingId || isAdding}
            onClick={() => addTier({ tiers: [tier] })}
          />
        ))}
      </div>

      {!universities.length ? (
        <EmptyState>
          No universities are tagged yet. Use "Apply default lists" to start from the built-in
          lists, then adjust.
        </EmptyState>
      ) : (
        <TableShell minWidth="min-w-[720px]">
          <thead>
            <tr className={headRow}>
              <th className={leftHead}>University</th>
              <th className={leftHead}>Tiers (click to toggle)</th>
              <th className="px-4 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {universities.map((university) => (
              <TierRow key={university._id} university={university} onSaved={refetch} />
            ))}
          </tbody>
        </TableShell>
      )}
    </div>
  );
};

export default TiersTab;
